"use server";

import { redirect } from "next/navigation";
import { z } from "zod";
import {
  clearSession,
  createSession,
  hashPassword,
  requireUser,
  verifyPassword,
} from "@/lib/auth";
import { issueEmailToken } from "@/lib/email-verify";
import { mailReady, notifyClientRegistered, notifyResetPassword, notifyVerifyEmail } from "@/lib/mail";
import { prisma } from "@/lib/prisma";
import { getSettings } from "@/lib/settings";

const loginSchema = z.object({
  email: z.string().email("Email tidak valid"),
  password: z.string().min(1, "Password wajib diisi"),
});

const registerSchema = z.object({
  name: z.string().min(3, "Nama minimal 3 karakter"),
  email: z.string().email("Email tidak valid"),
  phone: z.string().optional(),
  password: z.string().min(6, "Password minimal 6 karakter"),
});

async function sendVerification(user: { id: string; name: string; email: string; phone?: string | null }) {
  const token = await issueEmailToken(user.id);
  await notifyVerifyEmail({ name: user.name, email: user.email, phone: user.phone }, token);
}

export async function loginAction(_prev: unknown, formData: FormData) {
  const parsed = loginSchema.safeParse({
    email: String(formData.get("email") || "").trim(),
    password: String(formData.get("password") || ""),
  });
  if (!parsed.success) {
    return { error: parsed.error.issues[0]?.message || "Data tidak valid" };
  }

  const user = await prisma.user.findUnique({
    where: { email: parsed.data.email.toLowerCase() },
  });
  if (!user || !(await verifyPassword(parsed.data.password, user.passwordHash))) {
    return { error: "Email atau password salah." };
  }
  if (user.status !== "ACTIVE") {
    return { error: "Akun ini ditangguhkan. Hubungi admin." };
  }
  if (user.role !== "ADMIN" && !user.emailVerified) {
    const settings = await getSettings();
    if (mailReady(settings) && settings.mail_verify_enabled === "on") {
      try {
        await sendVerification(user);
      } catch (error) {
        console.error("Gagal mengirim ulang verifikasi:", error);
      }
      return { error: "Email belum diverifikasi. Kami kirim ulang tautan ke inbox Anda." };
    }
    await prisma.user.update({
      where: { id: user.id },
      data: { emailVerified: new Date() },
    });
  }

  await createSession({
    id: user.id,
    name: user.name,
    email: user.email,
    role: user.role === "ADMIN" ? "ADMIN" : "CLIENT",
  });

  const next = String(formData.get("next") || "");
  if (user.role === "ADMIN") redirect(next.startsWith("/admin") ? next : "/admin");
  redirect(next.startsWith("/dashboard") ? next : "/dashboard");
}

export async function registerAction(_prev: unknown, formData: FormData) {
  const parsed = registerSchema.safeParse({
    name: String(formData.get("name") || "").trim(),
    email: String(formData.get("email") || "").trim(),
    phone: String(formData.get("phone") || "").trim(),
    password: String(formData.get("password") || ""),
  });
  if (!parsed.success) {
    return { error: parsed.error.issues[0]?.message || "Data tidak valid" };
  }

  const email = parsed.data.email.toLowerCase();
  const exists = await prisma.user.findUnique({ where: { email } });
  if (exists) return { error: "Email sudah terdaftar." };

  const settings = await getSettings();
  const needVerify = mailReady(settings) && settings.mail_verify_enabled === "on";
  const user = await prisma.user.create({
    data: {
      name: parsed.data.name,
      email,
      phone: parsed.data.phone || null,
      passwordHash: await hashPassword(parsed.data.password),
      role: "CLIENT",
      emailVerified: needVerify ? null : new Date(),
    },
  });

  await notifyClientRegistered(
    {
      name: user.name,
      email: user.email,
      phone: user.phone,
    },
    { welcome: !needVerify },
  );

  if (needVerify) {
    try {
      await sendVerification(user);
    } catch (error) {
      console.error("Gagal mengirim email verifikasi:", error);
      return { error: "Akun dibuat, tetapi email verifikasi gagal dikirim. Coba masuk untuk kirim ulang." };
    }
    redirect("/login?ok=verify");
  }

  await createSession({
    id: user.id,
    name: user.name,
    email: user.email,
    role: "CLIENT",
  });
  redirect("/dashboard");
}

export async function logoutAction() {
  await clearSession();
  redirect("/");
}

export async function updateProfileAction(_prev: unknown, formData: FormData) {
  const user = await requireUser();
  const name = String(formData.get("name") || "").trim();
  const phone = String(formData.get("phone") || "").trim();
  if (name.length < 3) return { error: "Nama minimal 3 karakter." };

  await prisma.user.update({
    where: { id: user.id },
    data: { name, phone: phone || null },
  });
  await createSession({ ...user, name });
  redirect("/dashboard/profil?ok=profil");
}

export async function changePasswordAction(_prev: unknown, formData: FormData) {
  const session = await requireUser();
  const current = String(formData.get("current") || "");
  const next = String(formData.get("next") || "");
  if (next.length < 6) return { error: "Password baru minimal 6 karakter." };

  const user = await prisma.user.findUnique({ where: { id: session.id } });
  if (!user || !(await verifyPassword(current, user.passwordHash))) {
    return { error: "Password saat ini salah." };
  }

  await prisma.user.update({
    where: { id: user.id },
    data: { passwordHash: await hashPassword(next) },
  });
  redirect("/dashboard/profil?ok=password");
}

export async function requestPasswordResetAction(_prev: unknown, formData: FormData) {
  const parsed = z
    .object({ email: z.string().email("Email tidak valid") })
    .safeParse({ email: String(formData.get("email") || "").trim() });
  if (!parsed.success) {
    return { error: parsed.error.issues[0]?.message || "Email tidak valid" };
  }

  const settings = await getSettings();
  if (!mailReady(settings) || settings.mail_reset_enabled !== "on") {
    return { error: "Layanan reset password belum aktif. Hubungi admin." };
  }

  const email = parsed.data.email.toLowerCase();
  const user = await prisma.user.findUnique({ where: { email } });
  if (user && user.status === "ACTIVE") {
    try {
      const token = await issueEmailToken(user.id, "RESET");
      const sent = await notifyResetPassword(
        { name: user.name, email: user.email, phone: user.phone },
        token,
      );
      if (!sent) return { error: "Gagal mengirim email reset. Periksa SMTP atau coba lagi." };
    } catch (error) {
      console.error("Gagal mengirim email reset password:", error);
      return { error: "Gagal mengirim email reset. Coba lagi beberapa saat." };
    }
  }
  redirect("/lupa-password?ok=resetmail");
}

export async function resetPasswordAction(_prev: unknown, formData: FormData) {
  const token = String(formData.get("token") || "");
  const password = String(formData.get("password") || "");
  const confirm = String(formData.get("confirm") || "");
  if (!token) return { error: "Tautan reset tidak valid." };
  if (password.length < 6) return { error: "Password baru minimal 6 karakter." };
  if (password !== confirm) return { error: "Konfirmasi password tidak sama." };

  const row = await prisma.emailToken.findUnique({ where: { token } });
  if (!row || row.purpose !== "RESET") return { error: "Tautan reset tidak valid." };
  if (row.expiresAt.getTime() < Date.now()) {
    await prisma.emailToken.delete({ where: { id: row.id } });
    return { error: "Tautan reset sudah kedaluwarsa. Minta tautan baru." };
  }

  const user = await prisma.user.findUnique({ where: { id: row.userId } });
  if (!user || user.status !== "ACTIVE") return { error: "Akun tidak dapat direset." };

  await prisma.user.update({
    where: { id: user.id },
    data: {
      passwordHash: await hashPassword(password),
      emailVerified: user.emailVerified ?? new Date(),
    },
  });
  await prisma.emailToken.deleteMany({ where: { userId: user.id, purpose: "RESET" } });
  redirect("/login?ok=reset");
}
